import {
    Grid,
    Button,
    Flex,
    Label,
    useStyles,
    Avatar,
    Caption,
    pxToRem,
} from '@archipro-design/aria';

import * as S from './LocationDetail.mobile.style';
import type {
    ContactLoaderData,
    LocationDataType,
    ProfessionalInitialType,
} from '@modules/professional/type/contact-page-types';
import { MapPinLine } from '@archipro-design/icons';

import {
    getFullAddress,
    getLocationProfessional,
} from '@modules/professional/util/contact-page-helper';
import { useLoaderData, useNavigate } from '@remix-run/react';
import { useSaveToDesignBoard } from '~/modules/design-board/hook/use-save-to-design-board';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import ClickToView from '~/modules/root/component/click-to-view/ClickToView';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';

interface ItemProps {
    location: LocationDataType;
    professional: ProfessionalInitialType;
}

export const LocationDetailMobile: React.FC<ItemProps> = ({
    location,
    professional,
}) => {
    const { css } = useStyles();
    const navigate = useNavigate();
    const loaderData = useLoaderData<ContactLoaderData>();
    const linkedProfiles =
        loaderData?.linkedProfiles as ContactLoaderData['linkedProfiles'];

    const onViewProfile = (
        e: React.SyntheticEvent<HTMLElement, Event>,
        routePath: string
    ) => {
        e.preventDefault();
        navigate(routePath);
    };

    const locationProfessional = getLocationProfessional(
        location,
        professional,
        linkedProfiles
    );

    const { favouriteItem } = useSaveToDesignBoard({
        itemType: 'ProfessionalPin',
        itemId: locationProfessional.ID,
    });

    const { openEnquiryModal } = useEnquiryModal({
        professional: professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        includeBranch: true,
    });

    return (
        <>
            <Flex className={css(S.LocationDetailWrap)}>
                <div className={css(S.LocationAvatar)}>
                    <Avatar
                        image={{
                            src: locationProfessional.CompanyLogo ?? '',
                            alt: `${locationProfessional.Title} company logo`,
                        }}
                        size={80}
                        imageFit={'contain'}
                        variables={{
                            avatarImageBackgroundColor:
                                locationProfessional.LogoBackgroundColor,
                            borderWidth: '0',
                            avatarBackgroundColor:
                                locationProfessional.LogoBackgroundColor,
                            borderRadius: '0',
                        }}
                    />
                </div>

                <Grid columns={1}>
                    <Flex
                        space="between"
                        hAlign="end"
                        className={css(S.LocationType)}
                    >
                        <div>{location.Type}</div>
                        {location.distance !== undefined && (
                            <>
                                <Label variant="02">
                                    {`${location.distance} km`}
                                </Label>
                                <div className="map-pin">
                                    <MapPinLine size="2x" />
                                </div>
                            </>
                        )}
                    </Flex>

                    <div className={css(S.LocationTitle)}>
                        <Caption variant="03">{location.Title}</Caption>
                    </div>

                    <Flex className={css(S.LocationAddress)}>
                        <ClickToView
                            fulltext={getFullAddress(location)}
                            coordinates={location.MapCoordinates}
                            clickLabel="Address"
                            type={'Location'}
                            label={professional.Title}
                            buttonTrackingID={
                                locationProfessional.PageType.toLowerCase() +
                                '-contact-accordion-address-reveal'
                            }
                            revealKey={`professional-${locationProfessional.ID}`}
                            revealSubKey={location.ID.toString()}
                        />
                    </Flex>
                    <Flex className={css(S.LocationPhone)}>
                        <ClickToView
                            fulltext={location.Phone || ''}
                            clickLabel="Phone number"
                            type={'Phone'}
                            label={professional.Title}
                            buttonTrackingID={
                                locationProfessional.PageType.toLowerCase() +
                                '-library-location-phone-reveal'
                            }
                            revealKey={`professional-${locationProfessional.ID}-${location.ID}`}
                        />
                    </Flex>

                    {location.AllowsPickup && (
                        <Flex className={css(S.ClickAndCollectAvailable)}>
                            <Label>Click & collect available</Label>
                        </Flex>
                    )}

                    <Grid
                        columns={`1fr ${pxToRem(48)} 1fr`}
                        className={css(S.EnquiryAndSaveWrapper)}
                    >
                        <div>
                            <Button
                                color="dark"
                                size={24}
                                className={css(S.EnquireButton)}
                                variables={{
                                    darkButtonBackgroundColorNormal: '#222222',
                                }}
                                onClick={() => openEnquiryModal()}
                            >
                                Enquire
                            </Button>
                        </div>
                        <SaveToDesignBoard
                            {...favouriteItem}
                            heartButton={{
                                size: 16,
                                heartSize: '1x',
                                className: css(S.SaveToDesignBoardButton),
                            }}
                        />

                        <Flex hAlign="center">
                            {locationProfessional.ID !== professional.ID && (
                                <Button
                                    as={'a'}
                                    href={`/professional/${locationProfessional.URLSegment}`}
                                    color="dark"
                                    transparent={true}
                                    size={16}
                                    variables={{
                                        buttonPadding: 0,
                                    }}
                                    className={css(S.ViewProfileButton)}
                                    onClick={(e) =>
                                        onViewProfile(
                                            e,
                                            `/professional/${locationProfessional.URLSegment}`
                                        )
                                    }
                                >
                                    View Profile
                                </Button>
                            )}
                        </Flex>
                    </Grid>
                </Grid>
            </Flex>
        </>
    );
};
